import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import Modal from '@/components/atoms/Modal'
import { cohortsRepository } from '@/repositories'
import profileService from '@/services/profileService'
import { modalCreators } from '@/store/ducks/modal'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { Dialog } from '@headlessui/react'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import utc from 'dayjs/plugin/utc.js'

dayjs.extend(utc)
dayjs.locale('pt-br')

interface ProgressCheckProps {
	disabledOnClose?: boolean
}

export function ProgressCheck({ disabledOnClose }: ProgressCheckProps) {
	const {
		modal: { modalInfo },
	} = useAppSelector((state) => state)

	const dispatch = useAppDispatch()

	const {
		execute: editProfile,
		isLoading,
		isSuccess,
	} = profileService.editMutation()

	const handleCloseModal = async () => {
		await editProfile({ show_modal_progress_check: false })
	}

	const handleSuccessModal = async () => {
		await dispatch(cohortsRepository.util.invalidateTags(['ProgressCheck']))
		dispatch(modalCreators.closeModal())
	}

	useEffect(() => {
		if (isSuccess) handleSuccessModal()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess])

	return (
		<Modal
			name='progressCheckModal'
			disabledOnClose={disabledOnClose}
			onClose={handleCloseModal}
			className='relative overflow-hidden py-0 md:py-0 px-0 max-w-[531px] border-[3px] rounded-2xl border-brand-primary'
		>
			<div className='absolute top-0 right-0'>
				<img
					src={`/imgs/admin/modals/whatsapp-top-right.png`}
					width='243px'
					height='auto'
					alt='Imagem de spray no modal whatsapp'
				/>
			</div>

			<div className='flex flex-col h-full max-h-[_calc(100vh_-_80px)]'>
				<div id='drawer-content' className='flex-grow overflow-y-auto'>
					<div className='flex flex-col gap-5 py-8 px-8 md:px-12'>
						<Icon name='star-modal' width='88' height='88' />
						<div className='flex flex-col gap-8'>
							<div className='flex flex-col gap-8'>
								<div className='flex flex-col gap-4'>
									<span className='text-start text-[#EDE5FC] font-bold uppercase'>
										PROGRESS CHECK DISPONÍVEL
									</span>
									<Dialog.Title
										as='h3'
										className='text-start max-w-[435px] text-[34px] font-bold leading-[110%] tracking-tight text-zinc-50'
									>
										Algumas dicas para seu primeiro Progress Check
									</Dialog.Title>
									<p className='text-start w-full max-w-[417px] font-medium text-base text-zinc-50 leading-[140%]'>
										Esse teste de progressão foi criado para avaliar o quanto
										você está aproveitando os conteúdos dos últimos quatro
										ciclos de estudo
									</p>
									<div className='flex flex-col md:flex-row border-b border-white/20 py-4 gap-4 md:gap-14'>
										<div className='flex gap-2 items-center'>
											<Icon name='document' width='24' height='24' />
											<p className='text-start w-full text-lg font-medium text-zinc-50 leading-[140%]'>
												20 questões
											</p>
										</div>
										<div className='flex gap-2 items-center'>
											<Icon name='clock' width='24' height='24' />
											<p className='text-start w-full text-lg font-medium text-zinc-50 leading-[140%]'>
												20-30 minutos
											</p>
										</div>
									</div>
								</div>
								<div className='flex flex-col gap-6 pb-10'>
									<div className='flex gap-4'>
										<Icon
											name='check-content'
											width='20'
											height='20'
											className='mt-1'
										/>
										<p className='text-start w-full text-base text-zinc-50 leading-[140%]'>
											Você pode refazê-lo após 24 horas caso o resultado não
											tenha sido satisfatório. Aproveite esse tempo para revisar
											os conteúdos.
										</p>
									</div>
									<div className='flex gap-4'>
										<Icon
											name='check-content'
											width='20'
											height='20'
											className='mt-1'
										/>
										{(() => {
											if (modalInfo?.data_final) {
												return (
													<p className='text-start w-full text-base text-zinc-50 leading-[140%]'>
														O teste estará disponível até{' '}
														<b>
															dia{' '}
															{dayjs(modalInfo?.data_final).format(
																'DD/MM/YYYY'
															)}
														</b>
														. Por isso, planeje-se para realizá-lo com calma e
														não deixe para a última hora.
													</p>
												)
											}
											return (
												<p className='text-start w-full text-base text-zinc-50 leading-[140%]'>
													Planeje-se para realizá-lo com calma e não deixe para
													a última hora.
												</p>
											)
										})()}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='flex-shrink-0 w-full flex justify-end items-center px-6 h-[80px] bg-[#1D143C]'>
					<Button
						type='button'
						onClick={handleCloseModal}
						className='w-full max-w-[152px] rounded-full h-[40px]'
						isLoading={isLoading}
					>
						Entendido!
					</Button>
				</div>
			</div>
		</Modal>
	)
}
